import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from '@/lib/caixaToast';
import {
  type MotivoDesperdicio,
  createDesperdicioFromCaixa,
  fetchResponsaveisDesperdicio,
} from '@caixa/lib/api';
import type { ItemCarrinho } from '@caixa/types';

const MOTIVOS: { value: MotivoDesperdicio; label: string }[] = [
  { value: 'consumo_interno', label: 'Consumo interno' },
  { value: 'vencimento', label: 'Vencimento' },
  { value: 'brinde_propaganda', label: 'Brinde/Propaganda' },
  { value: 'cortesia_avaliacao', label: 'Cortesia/Avaliação' },
  { value: 'avaria', label: 'Avaria' },
];

interface DesperdicioFromCartModalProps {
  open: boolean;
  items: ItemCarrinho[];
  total: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function DesperdicioFromCartModal({
  open,
  items,
  total,
  onClose,
  onSuccess,
}: DesperdicioFromCartModalProps) {
  const [responsavel, setResponsavel] = useState('');
  const [motivo, setMotivo] = useState<MotivoDesperdicio>('consumo_interno');
  const [saving, setSaving] = useState(false);
  const [sugestoes, setSugestoes] = useState<string[]>([]);
  const [responsaveisLoaded, setResponsaveisLoaded] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    fetchResponsaveisDesperdicio().then(setResponsaveisLoaded);
  }, [open]);

  const filteredSuggestions = useMemo(() => {
    if (!responsavel.trim()) return responsaveisLoaded.slice(0, 15);
    const q = responsavel.trim().toLowerCase();
    return responsaveisLoaded.filter((n) => n.toLowerCase().startsWith(q)).slice(0, 15);
  }, [responsavel, responsaveisLoaded]);

  useEffect(() => {
    setSugestoes(filteredSuggestions);
  }, [filteredSuggestions]);

  useEffect(() => {
    if (open) {
      setResponsavel('');
      setMotivo('consumo_interno');
      setSaving(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const canConfirm = responsavel.trim().length > 0 && items.length > 0;

  const handleConfirm = async () => {
    if (!canConfirm || saving) return;
    setSaving(true);
    try {
      await createDesperdicioFromCaixa({
        responsavel_nome: responsavel.trim(),
        motivo,
        itens: items.map((i) => ({
          produto_id: i.cod,
          nome: i.nome,
          quantidade: i.quantidade,
          preco_unitario: i.precoUnitario,
        })),
        total,
      });
      onSuccess();
      onClose();
      toast.success('Desperdício registrado!');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha de conexão com a API do sistema. Verifique login e CORS.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="caixa-overlay caixa-overlay--70"
      role="dialog"
      aria-modal="true"
      aria-labelledby="desperdicio-cart-title"
    >
      <div className="caixa-modal caixa-modal--md caixa-modal--stack">
        <div className="caixa-modal-section--head">
          <h2 id="desperdicio-cart-title" className="caixa-modal-title">
            Registrar como desperdício
          </h2>
        </div>
        <div className="caixa-modal-section--body">
          <div className="caixa-field">
            <label htmlFor="desperdicio-responsavel">Nome do responsável *</label>
            <div className="relative">
              <input
                id="desperdicio-responsavel"
                ref={inputRef}
                type="text"
                value={responsavel}
                onChange={(e) => {
                  setResponsavel(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Quem consumiu/recebeu"
                className="caixa-input"
              />
              {showSuggestions && sugestoes.length > 0 && (
                <ul className="absolute z-10 top-full left-0 right-0 mt-1 rounded-md bg-[#18181b] border border-[#3a3a3a] max-h-40 overflow-y-auto">
                  {sugestoes.map((nome) => (
                    <li key={nome}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-[#f5f5f5] hover:bg-[#333333]"
                        onClick={() => {
                          setResponsavel(nome);
                          setShowSuggestions(false);
                        }}
                      >
                        {nome}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="caixa-field" style={{ marginBottom: 0 }}>
            <label htmlFor="desperdicio-motivo">Motivo *</label>
            <select
              id="desperdicio-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value as MotivoDesperdicio)}
              className="caixa-input"
            >
              {MOTIVOS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="caixa-modal-section--foot">
          <button
            type="button"
            onClick={onClose}
            className="caixa-btn-outline"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm || saving}
            className="caixa-btn-success"
          >
            {saving ? 'Salvando...' : 'Confirmar Desperdício'}
          </button>
        </div>
      </div>
    </div>
  );
}
